import { Component } from '@theme/component';

/**
 * A lightweight product image lightbox.
 *
 * Shows one image at a time in a contained, centered popout (not full-screen),
 * with prev/next arrows, a thumbnail strip, keyboard (left/right/escape) and
 * touch-swipe navigation. Wraps around at the ends.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} slides - The slide elements (one per media).
 * @property {HTMLElement} [stage] - The image stage (swipe target).
 * @property {HTMLElement} [thumbs] - The thumbnail strip container.
 *
 * @extends Component<Refs>
 */
export class LightboxDialog extends Component {
  requiredRefs = ['dialog', 'slides'];

  #index = 0;
  #touchStartX = null;

  get #count() {
    return this.refs.slides.length;
  }

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('close', this.#handleClose);
    this.refs.stage?.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.refs.stage?.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('close', this.#handleClose);
    this.refs.stage?.removeEventListener('touchstart', this.#handleTouchStart);
    this.refs.stage?.removeEventListener('touchend', this.#handleTouchEnd);
  }

  /**
   * Opens the lightbox at a given media index.
   * @param {number|string} index - The index of the media to show.
   * @param {Event} [event] - The triggering event.
   */
  open(index, event) {
    // On small screens the gallery is already swipeable, so skip the lightbox entirely.
    if (window.matchMedia('(max-width: 749px)').matches) return;
    event?.preventDefault();
    this.#show(Number(index) || 0);
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
  }

  close() {
    if (this.refs.dialog.open) this.refs.dialog.close();
  }

  next() {
    this.#show(this.#index + 1);
  }

  prev() {
    this.#show(this.#index - 1);
  }

  /**
   * Jumps to a specific index (thumbnail click).
   * @param {number|string} index - The index to show.
   */
  select(index) {
    this.#show(Number(index));
  }

  /**
   * Shows the slide at the given index, wrapping around at the ends.
   * @param {number} index - The target index.
   */
  #show(index) {
    const count = this.#count;
    if (!count) return;

    this.#index = ((index % count) + count) % count;

    this.refs.slides.forEach((slide, i) => {
      slide.hidden = i !== this.#index;
    });

    const { thumbs } = this.refs;
    if (!thumbs) return;

    const buttons = Array.from(thumbs.querySelectorAll('button'));
    buttons.forEach((button, i) => {
      // Use aria-current (not aria-selected): a plain <button> does not allow aria-selected.
      if (i === this.#index) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
    buttons[this.#index]?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }

  /**
   * Closes the lightbox when the backdrop (the dialog's own area) is clicked.
   * @param {MouseEvent} event - The click event.
   */
  handleBackdrop(event) {
    if (event.target === this.refs.dialog) this.close();
  }

  /**
   * Keyboard navigation. Escape is handled natively by the dialog.
   * @param {KeyboardEvent} event - The keydown event.
   */
  handleKeyDown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  }

  #handleClose = () => {
    document.documentElement.removeAttribute('scroll-lock');
  };

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    this.#touchStartX = event.changedTouches[0]?.clientX ?? null;
  };

  /** @param {TouchEvent} event */
  #handleTouchEnd = (event) => {
    if (this.#touchStartX == null) return;
    const deltaX = (event.changedTouches[0]?.clientX ?? this.#touchStartX) - this.#touchStartX;
    if (Math.abs(deltaX) > 40) {
      deltaX < 0 ? this.next() : this.prev();
    }
    this.#touchStartX = null;
  };
}

if (!customElements.get('lightbox-dialog')) {
  customElements.define('lightbox-dialog', LightboxDialog);
}
